/**
 * Thin React 17 wrapper around Video.js (#932).
 *
 * Replaces the deprecated Vime player. Video.js ships transpiled dist
 * bundles (no webpack-4 parse issues), has no React peer requirement, and
 * plays HLS out of the box via the bundled @videojs/http-streaming.
 * Also used by the public /embed/video page.
 */
import React, { useEffect, useRef } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

export type VideoTrack = {
  src: string;
  kind: string;
  label: string;
  srcLang: string;
  default?: boolean;
};

type VideoJsPlayerProps = {
  src: string;
  poster?: string;
  tracks?: VideoTrack[];
  autoplay?: boolean;
  fluid?: boolean;
  /** Fill the parent container (embed page) instead of aspect-ratio sizing. */
  fill?: boolean;
};

const sourceTypeFor = (src: string): string => {
  // Callers type `src` as a string, but a failed URL lookup once arrived here
  // as an error object and `split` took the whole page down. Fall back to the
  // default type instead; the player then reports the bad source itself.
  if (typeof src !== 'string') return 'video/mp4';
  const path = src.split('?')[0].toLowerCase();
  if (path.endsWith('.m3u8')) return 'application/x-mpegURL';
  if (path.endsWith('.webm')) return 'video/webm';
  return 'video/mp4';
};

export const VideoJsPlayer: React.FC<VideoJsPlayerProps> = ({
  src,
  poster,
  tracks = [],
  autoplay = false,
  fluid = true,
  fill = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    if (!playerRef.current) {
      // video.js replaces the element it mounts on, so give it a child our
      // container owns — React never reconciles inside containerRef.
      const videoElement = document.createElement('video-js');
      videoElement.classList.add('vjs-big-play-centered');
      containerRef.current.appendChild(videoElement);

      playerRef.current = videojs(videoElement, {
        controls: true,
        autoplay,
        preload: 'metadata',
        fluid: fill ? false : fluid,
        fill,
        playsinline: true,
        poster,
        sources: [{ src, type: sourceTypeFor(src) }],
        html5: {
          vhs: { overrideNative: false },
        },
      });
    } else {
      const player = playerRef.current;
      player.poster(poster || '');
      player.src([{ src, type: sourceTypeFor(src) }]);
    }

    // (re)attach subtitle tracks
    const player = playerRef.current;
    const existing = player.remoteTextTracks();
    for (let i = existing.length - 1; i >= 0; i -= 1) {
      player.removeRemoteTextTrack(existing[i]);
    }
    tracks.forEach((track) => {
      player.addRemoteTextTrack(
        {
          kind: track.kind,
          src: track.src,
          srclang: track.srcLang,
          label: track.label,
          default: track.default,
        },
        false,
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, poster, JSON.stringify(tracks.map((t) => t.src))]);

  useEffect(
    () => () => {
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    },
    [],
  );

  return <div style={{ width: '100%' }} data-vjs-player ref={containerRef} />;
};

export default VideoJsPlayer;
